import dayjs from 'dayjs';

/** period 의 양 끝 값 — dayjs 가 그대로 받는 것들 */
type DateBound = string | number | Date | null | undefined;

/**
 * 조회 기간. **네 모양을 다 받는다.**
 * `{ 0, 1 }` 갈래가 있는 것은 배열처럼 생긴 객체가 실제로 오기 때문이다.
 */
export type ChartPeriod =
  | DateBound[]
  | { start?: DateBound; end?: DateBound; 0?: DateBound; 1?: DateBound }
  | null
  | undefined;

/** 채널별 시리즈 한 줄 */
export type DailyChannelSeries = { key?: string; name: string; data?: number[] };

/**
 * period → YYYY-MM-DD 일자 배열 (양 끝 inclusive)
 * @param period `[start, end]` 또는 `{ start, end }`
 */
export function buildDailyDateCategories(period: ChartPeriod): string[] {
  let periodStart: DateBound = null;
  let periodEnd: DateBound = null;

  if (Array.isArray(period)) {
    periodStart = period[0];
    periodEnd = period[1] ?? period[period.length - 1];
  } else if (period && typeof period === 'object') {
    periodStart = period.start ?? period[0];
    periodEnd = period.end ?? period[1];
  }

  const start = periodStart ? dayjs(periodStart).startOf('day') : null;
  const end = periodEnd ? dayjs(periodEnd).startOf('day') : null;
  if (!start?.isValid() || !end?.isValid() || end.isBefore(start)) {
    return [];
  }

  const categories = [];
  let cursor = start;
  while (!cursor.isAfter(end, 'day')) {
    categories.push(cursor.format('YYYY-MM-DD'));
    cursor = cursor.add(1, 'day');
  }
  return categories;
}

/** 멘션 추이 차트 — 조회 기간 전체 일자로 패딩 (데이터 없는 날은 0) */
export function fillDailyMentionsChartToPeriod({
  categories = [],
  mentions = [],
  reach = [],
  period,
}: {
  categories?: string[];
  mentions?: number[];
  reach?: number[];
  period?: ChartPeriod;
} = {}) {
  const fullCategories = buildDailyDateCategories(period);
  if (fullCategories.length === 0) {
    return {
      categories: [...categories],
      mentions: [...mentions],
      reach: [...reach],
    };
  }

  const mentionMap = new Map<string, number>();
  const reachMap = new Map<string, number>();
  categories.forEach((day, idx) => {
    mentionMap.set(day, Number(mentions[idx] ?? 0));
    reachMap.set(day, Number(reach[idx] ?? 0));
  });

  return {
    categories: fullCategories,
    mentions: fullCategories.map((day) => mentionMap.get(day) ?? 0),
    reach: fullCategories.map((day) => reachMap.get(day) ?? 0),
  };
}

/** 채널별 멘션 추이 — 조회 기간 전체 일자로 패딩 (데이터 없는 날·채널은 0) */
export function fillDailyChannelSeriesToPeriod({
  categories = [],
  series = [],
  period,
}: {
  categories?: string[];
  series?: DailyChannelSeries[];
  period?: ChartPeriod;
} = {}) {
  const fullCategories = buildDailyDateCategories(period);
  if (fullCategories.length === 0) {
    return {
      categories: [...categories],
      series: series.map((s) => ({
        ...s,
        data: [...(s.data ?? [])],
      })),
    };
  }

  const indexByDay = new Map<string, number>(categories.map((day, idx) => [day, idx]));
  const filledSeries = series.map((item) => ({
    ...item,
    data: fullCategories.map((day) => {
      const idx = indexByDay.get(day);
      return idx === undefined ? 0 : Number(item.data?.[idx] ?? 0);
    }),
  }));

  return {
    categories: fullCategories,
    series: filledSeries,
  };
}

/**
 * 가로축의 날짜 칸 이름을 **짧게** — `YYYY-MM-DD` → `YY.MM.DD` · `YYYY-MM` → `YY.MM`.
 *
 * 🚨 날짜 축의 칸 이름은 **열 자**라 좁은 그림에서 ApexCharts 가 「2026…」으로 잘라 버린다
 *   (크리에이터 허브 「활동 추이」에서 열세 칸이 전부 같은 글자가 되어 아무것도 못 읽었다).
 *   해는 두 자리면 된다 — 한 그림 안에서 **해가 바뀌는 지점**만 보이면 되기 때문이다.
 * ⚠️ 날짜 모양이 아니면 **그대로 돌려준다** — 채널·브랜드 이름 축도 같은 길을 탄다.
 */
export function shortAxisDateLabel(label: unknown): string {
  const text = String(label ?? '');
  const ymd = /^(\d{4})-(\d{2})-(\d{2})$/.exec(text);
  if (ymd) return `${ymd[1].slice(2)}.${ymd[2]}.${ymd[3]}`;
  const ym = /^(\d{4})-(\d{2})$/.exec(text);
  if (ym) return `${ym[1].slice(2)}.${ym[2]}`;
  return text;
}
